import React, { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import OvStagedFileNotices from '../molecules/OvStagedFileNotices';
import OvVideoStagingRow from '../molecules/OvVideoStagingRow';
import OvVideoDropZone from '../molecules/OvVideoDropZone';
import OvVideoPlacementFields from '../molecules/OvVideoPlacementFields';
import { MAX_BULK_VIDEOS } from '../../../common/model/dto/video/video-constants';
import { isPlacementPositionValid } from '../../../common/utils/video-placement-utils';
import { stagedFieldsId } from '../../../common/utils/video-staging-utils';
import { useVideoStagingSession } from '../../../hooks/use-video-staging-session';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvVideoStagingFormProps {
  onFiles: (files: File[]) => void;
}

const OvVideoStagingForm: FC<OvVideoStagingFormProps> = ({ onFiles }) => {
  const { t } = useTranslation();
  const session = useVideoStagingSession();
  const summaryRef = useRef<HTMLHeadingElement>(null);
  const isDisabled = session.isSubmitting;

  useEffect(() => {
    summaryRef.current?.focus();
  }, []);

  return (
    <Form>
      <Toolbar>
        <Count ref={summaryRef} tabIndex={-1}>
          {t('videos.staging.videoCount', { count: session.staged.length })}
        </Count>
        <OvVideoDropZone
          variant="button"
          label={t('videos.staging.addFiles')}
          buttonLabel={t('videos.staging.addFiles')}
          hint={t('videos.staging.dropHere')}
          limits={t('videos.staging.limits', { max: MAX_BULK_VIDEOS })}
          isMultiple
          isDisabled={isDisabled}
          onFiles={onFiles}
        />
      </Toolbar>
      <Helper>{t('videos.staging.keptInTab')}</Helper>

      <OvStagedFileNotices
        rejected={session.rejected}
        duplicates={session.duplicates}
      />

      {session.staged.length > 0 && (
        <List aria-label={t('videos.staging.listLabel')}>
          {session.staged.map((item) => (
            <OvVideoStagingRow
              key={item.key}
              item={item}
              isDisabled={isDisabled}
              onTitleChange={session.setTitle}
              onDescriptionChange={session.setDescription}
              onOrientationChange={session.setOrientation}
              onRemove={session.remove}
            >
              <OvVideoPlacementFields
                id={stagedFieldsId(item.key)}
                values={item.placement}
                positionError={
                  isPlacementPositionValid(item.placement)
                    ? null
                    : t('videos.placement.positionInvalid')
                }
                isDisabled={isDisabled}
                onChange={(changes) => session.setPlacement(item.key, changes)}
              />
            </OvVideoStagingRow>
          ))}
        </List>
      )}
    </Form>
  );
};

export default OvVideoStagingForm;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Variables.spacing.SMALL};
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${Variables.spacing.SMALL};
`;

const Count = styled.h3`
  margin: 0;
  font-size: ${Variables.fontSizes.MEDIUM};

  &:focus {
    outline: none;
  }
`;

const Helper = styled.p`
  margin: 0;
  font-size: ${Variables.fontSizes.SMALL};
  color: ${Colours.OV_CHARCOAL};
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;
